import React, { useState, useEffect, useRef } from 'react';
import '../styles/components/FormComponents.scss';

// Create a global dropdown manager
const DropdownManager = {
  activeDropdown: null,
  setActiveDropdown(id) {
    // Close the previously active dropdown if it's different
    if (this.activeDropdown && this.activeDropdown !== id) {
      this.closeCallback && this.closeCallback();
    }
    this.activeDropdown = id;
  },
  closeCallback: null,
  registerCloseCallback(callback) {
    this.closeCallback = callback;
  },
  clearCloseCallback() {
    this.closeCallback = null;
  }
};

export const Dropdown = ({ 
  options, 
  placeholder = "Select an option",
  onChange,
  value,
  name,
  className = "",
  onArrowClick = null,
  allowCustomInput = false,
  isCustomInputOnly = false,
  isClearable = false
}) => {
  // Log the value prop for debugging
  console.log('Dropdown value received:', value);

  const [isOpen, setIsOpen] = useState(false);
  const [customInputValue, setCustomInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef(null);
  const customInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownId = useRef(`dropdown-${Math.random().toString(36).substr(2, 9)}`);
  
  // Filter options based on search input
  const filteredOptions = isCustomInputOnly ? [] : options.filter(option => 
    option.label.toLowerCase().includes(searchValue.toLowerCase())
  );
  
  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleClear = (e) => {
    e.stopPropagation(); // Prevent dropdown from opening
    e.preventDefault(); // Ensure no default action occurs
    console.log("Clearing selection");
    
    // Add a small delay to ensure the event doesn't conflict
    setTimeout(() => {
      // Call onChange with null to clear the selection
      onChange(null);
      
      // Close the dropdown when clearing
      setIsOpen(false);
    }, 10);
  };

  const handleArrowClick = (e) => {
    e.stopPropagation();
    if (onArrowClick && value) {
      // First trigger the callback to ensure search results are shown
      onArrowClick();
      
      // Then close all dropdowns by simulating a click outside
      setTimeout(() => {
        document.dispatchEvent(new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          view: window
        }));
      }, 100);
    } else {
      toggleDropdown();
    }
  };

  const toggleDropdown = () => {
    // If we're opening the dropdown, register our close callback
    if (!isOpen) {
      // Register this dropdown with the manager
      DropdownManager.setActiveDropdown(dropdownId.current);
      DropdownManager.registerCloseCallback(() => {
        setIsOpen(false);
      });
    } else {
      // We're closing, clear the callback
      DropdownManager.clearCloseCallback();
    }
    
    setIsOpen(!isOpen);
    
    // Reset search when opening
    if (!isOpen) {
      setSearchValue('');
      // Focus search input after a short delay to allow DOM to update
      setTimeout(() => {
        if (searchInputRef.current && !isCustomInputOnly) {
          searchInputRef.current.focus();
        }
      }, 50);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Handle custom input change
  const handleCustomInputChange = (e) => {
    setCustomInputValue(e.target.value);
  };

  // Submit custom input when Enter is pressed
  const handleCustomInputKeyDown = (e) => {
    if (e.key === 'Enter' && customInputValue.trim()) {
      e.preventDefault();
      const customOption = { value: customInputValue, label: customInputValue };
      onChange(customOption);
      setCustomInputValue('');
      setIsOpen(false);
    }
  };

  // Submit custom input when clicking submit button
  const handleCustomInputSubmit = () => {
    if (customInputValue.trim()) {
      const customOption = { value: customInputValue, label: customInputValue };
      onChange(customOption);
      setCustomInputValue('');
      setIsOpen(false);
    }
  };

  // Handle clicks outside of the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchValue('');
        if (DropdownManager.activeDropdown === dropdownId.current) {
          DropdownManager.setActiveDropdown(null);
          DropdownManager.clearCloseCallback();
        }
      }
    };

    // Add event listener when dropdown is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Log warning if value structure is invalid (removed automatic fixing to prevent loops)
  if (value && (value.label === undefined || value.value === undefined)) {
    console.warn('Dropdown received a value without proper structure:', value);
  }

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (DropdownManager.activeDropdown === dropdownId.current) {
        DropdownManager.setActiveDropdown(null);
        DropdownManager.clearCloseCallback();
      }
    };
  }, []);

  return (
    <div 
      className={`form-dropdown ${className} ${isOpen ? 'expanded' : ''}`} 
      ref={dropdownRef}
      data-dropdown-id={dropdownId.current}
    >
      <div 
        className={`form-dropdown__selected ${isOpen ? 'expanded' : ''}`}
        onClick={toggleDropdown}
      >
        <span>{value ? value.label : placeholder}</span>
        
        <div className="form-dropdown__icons">
          {/* Show clear button if we have a value and isClearable is true */}
          {isClearable && value && (
            <button 
              type="button"
              className="form-dropdown__clear-btn"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          {value && (
            <div 
              className={`form-dropdown__arrow ${isOpen ? 'open expanded' : ''}`}
              onClick={handleArrowClick}
            >
              <svg fill="#000000" height="200px" width="200px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 330 330" xmlSpace="preserve" transform="rotate(90)">
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <path id="XMLID_27_" d="M15,180h263.787l-49.394,49.394c-5.858,5.857-5.858,15.355,0,21.213C232.322,253.535,236.161,255,240,255 s7.678-1.465,10.606-4.394l75-75c5.858-5.857,5.858-15.355,0-21.213l-75-75c-5.857-5.857-15.355-5.857-21.213,0 c-5.858,5.857-5.858,15.355,0,21.213L278.787,150H15c-8.284,0-15,6.716-15,15S6.716,180,15,180z"></path>
                </g>
              </svg>
            </div>
          )}
        </div>
      </div>
      
      {isOpen && (
        <ul className={`form-dropdown__options ${isOpen ? 'visible' : ''}`}>
          {/* Search input at the top of options, only if not custom input only mode */}
          {!isCustomInputOnly && (
            <li className="form-dropdown__search-input">
              <input
                ref={searchInputRef}
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search..."
                onClick={(e) => e.stopPropagation()}
                className="form-dropdown__search-field"
              />
            </li>
          )}
          
          {/* Only show options list if not in custom input only mode */}
          {!isCustomInputOnly && filteredOptions.map((option, index) => (
            <li 
              key={index} 
              onClick={() => handleSelect(option)}
              className={value && value.value === option.value ? 'selected' : ''}
            >
              {option.label}
            </li>
          ))}
          
          {/* Show message when no options match search */}
          {!isCustomInputOnly && searchValue && filteredOptions.length === 0 && (
            <li className="form-dropdown__no-results">No options match your search</li>
          )}
          
          {/* Custom input option */}
          {(allowCustomInput || isCustomInputOnly) && (
            <li className="form-dropdown__custom-input">
              <input
                ref={customInputRef}
                type="text"
                value={customInputValue}
                onChange={handleCustomInputChange}
                onKeyDown={handleCustomInputKeyDown}
                placeholder="Type your own option..."
                onClick={(e) => e.stopPropagation()}
                autoFocus={isCustomInputOnly}
              />
              <button 
                className="form-dropdown__custom-submit"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCustomInputSubmit();
                }}
              >
                Add
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export const TextInput = ({
  placeholder,
  value,
  onChange,
  name,
  type = "text",
  className = ""
}) => {
  return (
    <div className={`form-input ${className}`}>
      <input 
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        name={name}
      />
    </div>
  );
};

export const Button = ({
  children,
  onClick,
  type = "button",
  className = "",
  arrowRight = false
}) => {
  return (
    <button 
      type={type}
      onClick={onClick}
      className={`form-button ${className} ${arrowRight ? 'with-arrow' : ''}`}
    >
      {children}
      {arrowRight && (
        <span className="arrow-right">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.16675 10H15.8334" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10.8334 5L15.8334 10L10.8334 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      )}
    </button>
  );
}; 